import type { CustomGraph, TriggerConfig } from '@/types/custom-playbooks';

/**
 * A Google Sheet trigger's columns are read downstream as `trigger.<key>`,
 * and a column's key follows its header. When a header edit moves keys, the
 * steps that read the old keys are rewritten to the new ones; a reference to
 * a key the sheet no longer has is flagged on its step.
 */

/** Old key -> new key, carried from a column edit to the graph by config. */
const pendingRenames = new WeakMap<object, ReadonlyMap<string, string>>();

/** Mark a trigger config with the column key moves its edit made. */
export function withColumnRenames<T extends object>(
  config: T,
  renames: ReadonlyMap<string, string>
): T {
  if (renames.size) pendingRenames.set(config, renames);
  return config;
}

/** The column key moves a trigger config carries, if its edit made any. */
export function columnRenamesOf(
  config: unknown
): ReadonlyMap<string, string> | undefined {
  return config && typeof config === 'object'
    ? pendingRenames.get(config)
    : undefined;
}

// `trigger.<key>` as a field id or inside a `{{ }}` tag, not part of a longer
// path such as `step.x.trigger.<key>`.
const REF = /(?<![\w.])trigger\.([a-z][a-z0-9_]*)(?![\w])/g;

function mapStrings(value: unknown, fn: (text: string) => string): unknown {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) {
    const next = value.map((item) => mapStrings(item, fn));
    return next.some((item, at) => item !== value[at]) ? next : value;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      const nextKey = fn(key);
      const nextItem = mapStrings(item, fn);
      if (nextKey !== key || nextItem !== item) changed = true;
      next[nextKey] = nextItem;
    }
    return changed ? next : value;
  }
  return value;
}

/** Every step but the trigger reads the moved keys under their new names,
 *  in one pass, so two keys that trade places stay correct. */
export function renameTriggerFieldRefs(
  graph: CustomGraph,
  triggerId: string,
  renames: ReadonlyMap<string, string>
): CustomGraph {
  const moves = [...renames].filter(([from, to]) => from !== to);
  if (!moves.length) return graph;
  const map = new Map(moves);
  const rewrite = (text: string) =>
    text.includes('trigger.')
      ? text.replace(REF, (whole, key: string) =>
          map.has(key) ? `trigger.${map.get(key)}` : whole
        )
      : text;
  let changed = false;
  const nodes = graph.nodes.map((node) => {
    if (node.id === triggerId) return node;
    const config = mapStrings(node.config, rewrite);
    if (config === node.config) return node;
    changed = true;
    return { ...node, config: config as typeof node.config };
  });
  return changed ? { ...graph, nodes } : graph;
}

/** The `trigger.<key>` reads a step makes that the sheet has no column for. */
// A Notion page's own fields, read beside its mapped properties
// (``PAGE_FIELDS`` in the API's notion_trigger.py).
const NOTION_PAGE_FIELDS = [
  'page_id',
  'page_url',
  'created_time',
  'last_edited_time',
];

/** The trigger.<key> fields a Sheet or Notion trigger gives its steps. */
function triggerFieldKeys(
  config: Partial<TriggerConfig> | undefined
): { source: 'google_sheet' | 'notion'; keys: Set<string> } | undefined {
  if (
    config?.source === 'google_sheet' &&
    config.kind === 'sheet_rows_added' &&
    Array.isArray(config.sheet?.columns)
  )
    return {
      source: 'google_sheet',
      keys: new Set(config.sheet.columns.map((column) => column.key)),
    };
  if (config?.source === 'notion' && Array.isArray(config.notion?.properties))
    return {
      source: 'notion',
      keys: new Set([
        ...config.notion.properties.map((property) => property.key),
        ...NOTION_PAGE_FIELDS,
      ]),
    };
  return undefined;
}

export function missingSheetColumnRefs(
  graph: CustomGraph
): { nodeId: string; field: string; source: 'google_sheet' | 'notion' }[] {
  const trigger = graph.nodes.find((node) => node.type === 'trigger');
  const fields = triggerFieldKeys(
    trigger?.config as Partial<TriggerConfig> | undefined
  );
  if (!trigger || !fields) return [];
  const { source, keys } = fields;
  const missing: {
    nodeId: string;
    field: string;
    source: 'google_sheet' | 'notion';
  }[] = [];
  for (const node of graph.nodes) {
    if (node.id === trigger.id) continue;
    const seen = new Set<string>();
    mapStrings(node.config, (text) => {
      for (const match of text.matchAll(REF))
        if (!keys.has(match[1])) seen.add(`trigger.${match[1]}`);
      return text;
    });
    for (const field of seen) missing.push({ nodeId: node.id, field, source });
  }
  return missing;
}
